import styled from "@emotion/styled";
import {
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	MenuItem,
	TableContainer,
	TextField
} from "@mui/material";

export const Section = styled.section({
	display: "flex",
	flexDirection: "column",
	gap: 8,
	minWidth: 0
});

export const SectionHeader = styled.div(({ theme }) => ({
	alignItems: "center",
	display: "flex",
	gap: 16,
	justifyContent: "space-between",

	[theme.breakpoints.down("sm")]: {
		alignItems: "flex-start",
		flexDirection: "column"
	}
}));

export const CasesPanel = styled.div(({ theme }) => ({
	backgroundColor: theme.project.colors.backgroundColors.main,
	border: theme.project.borders.presets.thinOutline,
	display: "flex",
	flexDirection: "column",
	minWidth: 0,
	padding: 8,

	[theme.breakpoints.down("sm")]: {
		padding: 0
	}
}));

export const CasesToolbar = styled.div(({ theme }) => ({
	alignItems: "flex-start",
	display: "flex",
	gap: 24,
	justifyContent: "space-between",
	paddingBottom: 8,

	[theme.breakpoints.down("md")]: {
		alignItems: "stretch",
		flexDirection: "column",
		gap: 12,
		padding: 12
	}
}));

export const CaseSummary = styled.div({
	display: "flex",
	flexDirection: "column",
	gap: 2
});

export const Heading = styled.h2(({ theme }) => ({
	...theme.project.typography.heading.h4,
	color: theme.project.colors.fontColors.main,
	fontWeight: 200,
	lineHeight: "124%",
	margin: 0
}));

export const Count = styled.p(({ theme }) => ({
	...theme.project.typography.body.body2,
	color: theme.project.colors.fontColors.main,
	fontWeight: 500,
	fontSize: "20px",
	lineHeight: "160%",
	letterSpacing: "0.15px",
	margin: 0
}));

export const AwaitingReplyNote = styled.p(({ theme }) => ({
	...theme.project.typography.caption,
	color: theme.project.colors.fontColors.secondary,
	margin: 0,
	fontWeight: 400,
	fontSize: "14px",
	lineHeight: "143%",
	letterSpacing: "0.17px"
}));

export const SubmitRequestButton = styled(Button)(({ theme }) => ({
	backgroundColor: theme.project.colors.allColors.hex.orange.orange500,
	borderRadius: 0,
	boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
	fontSize: 15,
	fontWeight: 500,
	lineHeight: "26px",
	letterSpacing: "0.46px",
	minHeight: 36,
	padding: "6px 18px",

	"&:hover": {
		backgroundColor: theme.project.colors.allColors.hex.orange.orange500,
		filter: "brightness(0.92)"
	},

	[theme.breakpoints.down("sm")]: {
		width: "100%"
	}
}));

export const SearchField = styled(TextField)(({ theme }) => ({
	flexShrink: 0,
	maxWidth: 320,
	width: "100%",

	"& .MuiInputBase-root": {
		borderRadius: 2,
		fontSize: 12,
		height: 34
	},

	"& .MuiSvgIcon-root": {
		fontSize: 20
	},

	[theme.breakpoints.down("md")]: {
		maxWidth: "none"
	}
}));

export const CasesTableContainer = styled(TableContainer)(({ theme }) => ({
	backgroundColor: theme.project.colors.backgroundColors.main,
	width: "100%",

	"& .MuiTableCell-root": {
		fontWeight: 400,
		fontSize: 14,
		lineHeight: "143%",
		letterSpacing: "0.17px",
		padding: "10px 12px"
	},

	"& .MuiTableCell-head": {
		fontWeight: 500,
		fontSize: 14,
		lineHeight: "24px",
		letterSpacing: "0.17px",
		padding: "7px 12px"
	}
}));

export const EmptyMessage = styled.p(({ theme }) => ({
	...theme.project.typography.body.body2,
	backgroundColor: theme.project.colors.backgroundColors.main,
	color: theme.project.colors.fontColors.secondary,
	margin: 0,
	padding: 24,
	textAlign: "center"
}));

const receivedStatusColors = {
	backgroundColor: "#ffffff",
	color: "#00739b",
	borderColor: "#00739b"
};

const statusColors: Record<string, { backgroundColor: string; color: string; borderColor: string }> = {
	"Awaiting Your Reply": { backgroundColor: "#f36c2d", color: "#ffffff", borderColor: "#f36c2d" },
	Escalated: { backgroundColor: "#d94f19", color: "#ffffff", borderColor: "#d94f19" },
	Open: { backgroundColor: "#a7cf3a", color: "#ffffff", borderColor: "#a7cf3a" },
	Received: receivedStatusColors,
	Resolved: { backgroundColor: "#e5e5e5", color: "#555555", borderColor: "#e5e5e5" }
};

export const StatusChip = styled(Chip, {
	shouldForwardProp: (property) => property !== "status"
})<{ status?: string | null }>(({ status }) => {
	const colors = statusColors[status ?? ""] ?? receivedStatusColors;

	return {
		...colors,
		border: status === "Received" ? `1px solid ${colors.borderColor}` : "none",
		borderRadius: 12,
		fontSize: 14,
		lineHeight: "18px",
		letterSpacing: "0.46px",
		fontWeight: 500,
		height: 24,

		"& .MuiChip-label": {
			padding: "0 7px"
		}
	};
});

export const ActionButton = styled(IconButton)(({ theme }) => ({
	border: `1px solid ${theme.project.colors.primaryColors.main}`,
	borderRadius: 4,
	color: theme.project.colors.primaryColors.main,
	height: 30,
	padding: 0,
	width: 30,

	"& .MuiSvgIcon-root": {
		fontSize: 18
	},

	"&:hover": {
		backgroundColor: theme.project.colors.backgroundColors.selected
	},

	"&.Mui-disabled": {
		borderColor: theme.project.colors.backgroundColors.disabled
	}
}));

export const CloseCaseMenuItem = styled(MenuItem)(({ theme }) => ({
	color: theme.project.colors.semanticColors.error
}));

export const ActionError = styled.div({
	marginBottom: 4
});

export const EscalateDialog = styled(Dialog)({
	"& .MuiBackdrop-root": {
		backgroundColor: "rgba(0, 0, 0, 0.18)"
	},

	"& .MuiDialog-paper": {
		borderRadius: 2,
		boxShadow: "0 12px 28px rgba(0, 0, 0, 0.28)",
		margin: 16,
		maxWidth: 532,
		width: "calc(100% - 32px)"
	}
});

export const EscalateDialogTitle = styled(DialogTitle)(({ theme }) => ({
	...theme.project.typography.heading.h6,
	color: theme.project.colors.fontColors.main,
	fontSize: 18,
	lineHeight: "24px",
	padding: "17px 20px 0",
	position: "relative"
}));

export const EscalateDialogCloseButton = styled(IconButton)({
	padding: 4,
	position: "absolute",
	right: 16,
	top: 13,

	"& .MuiSvgIcon-root": {
		fontSize: 20
	}
});

export const EscalateDialogContent = styled(DialogContent)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	gap: 12,
	overflowY: "visible",
	padding: "2px 20px 0",

	"&:first-of-type": {
		paddingTop: 2
	},

	"& > p": {
		...theme.project.typography.body.body2,
		color: theme.project.colors.fontColors.main,
		fontSize: 13,
		lineHeight: "18px",
		margin: 0
	},

	"& .MuiInputBase-root": {
		borderRadius: 2,
		fontSize: 13,
		height: 36
	},

	"& .MuiInputLabel-root": {
		fontSize: 13
	}
}));

export const EscalateDialogActions = styled(DialogActions)({
	display: "grid",
	gap: 14,
	gridTemplateColumns: "1fr 1fr",
	padding: "14px 20px 20px",

	"& > :not(style) ~ :not(style)": {
		marginLeft: 0
	}
});

export const EscalateDialogActionButton = styled(Button)({
	alignItems: "center",
	fontSize: 14,
	height: 38,
	justifyContent: "center",
	width: "100%"
});
